import { useState } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ControlledTextField, Dialog } from "@components";
import { AsyncPickerItem } from "@components/AsyncPicker/components/AsyncPickerItem/AsyncPickerItem";
import { UserItem } from "@components/AsyncPicker/components/UserItem";
import { inviteContractorQuery, removeProjectContractorQuery } from "@query";
import { CircularProgress, Grid } from "@material-ui/core";
import { emailRegex } from "@utils";

const inviteContractorSchema = (t) =>
  yup.object().shape({
    email: yup
      .string()
      .matches(emailRegex, t("errors.email.required"))
      .required(t("errors.email.required"))
      .max(64, t("errors.email.max")),
  });

export const InviteContractorDialog = ({
 isOpen,
 onClose,
 onInvite,
 projectUuid,
 contractorMembers,
 alreadyInvited,
 onContractorChange
}) => {
  const { t } = useTranslation();

  const [ isLoading, setIsLoading ] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(inviteContractorSchema(t)),
    defaultValues: {email: ""},
  });

  const handleAddContractor = async (member) => {
    if (!member?.email) return;
    try {
      const result = await inviteContractorQuery(member.email, [projectUuid]);
      if (result.hasError) {
        setError("email", {
          type: "manual",
          message: result.errorMessage || t("errors.email.invited"),
        });
        return;
      }
      onContractorChange && await onContractorChange();
    } catch (error) {
      setError("email", {
        type: "manual",
        message: error?.response?.data?.error || error.message,
      });
    }
  };

  const handleRemoveContractor = async (memberEmail) => {
    if (!memberEmail) return;
    try {
      await removeProjectContractorQuery(projectUuid, memberEmail);
      onContractorChange && await onContractorChange();
    } catch (error) {
      setError("email", {
        type: "manual",
        message: error?.response?.data?.error || error.message,
      });
    }
  };

  const submitHandler = async (data) => {
    setIsLoading(true);
    try {
      const result = await onInvite(data.email, projectUuid);
      if (result.hasError) {
        setError("email", {
          type: "manual",
          message: result.errorMessage || t("errors.email.invited"),
        });
      } else {
        reset();
        onClose();
      }
    } catch (error) {
      setError("email", {
        type: "manual",
        message: error.message || "Failed to invite contractor",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={t("dialogs.inviteContractor.title")}
      width={420}
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(submitHandler)}
            disabled={isLoading}
            icon={isLoading ? <CircularProgress size={24} color="inherit"/> : null}
          >
            {!isLoading ? t("common.send") : null}
          </Button>
        </>
      }
    >
      <p className="mb-4">{t("dialogs.inviteContractor.description")}</p>
      <Grid item container direction="column">
        {contractorMembers?.length > 0 && (
          <Grid item container direction="column" className="mb-4">
            {contractorMembers.map((member) => (
              <AsyncPickerItem
                key={member.id}
                data={member}
                idKey="email"
                labelKey="fullname"
                selectedItems={alreadyInvited || []}
                onAdd={handleAddContractor}
                onRemove={handleRemoveContractor}
                ContentItem={UserItem}
              />
            ))}
          </Grid>
        )}
        <Grid item>
          <ControlledTextField
            type="email"
            name="email"
            control={control}
            label={t("forms.board_invite.invite_user")}
            placeholder="contractor@example.com"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};

InviteContractorDialog.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onInvite: func.isRequired,
  projectUuid: string.isRequired,
  onContractorChange: func,
  contractorMembers: arrayOf(shape({
    id: number,
    email: string,
    fullname: string,
  })),
  alreadyInvited: arrayOf(shape({
    id: number,
  })),
};
